import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_BILL_DESCRIPTION_LENGTH,
  SUPPLIER_TRADE_LABELS,
  formatAmount,
  nextWaitingItem,
  parseAmount,
  streetLine,
  type Money,
} from '@promanage/core';
import { Button, Field, Input, Panel, PanelHeader, Select, Textarea, Amount } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { todayIso } from '../features/property/today';
import { ChosenInvoice, useAttachInvoice } from '../features/bills/BillInvoice';
import { InboxInvoice, useInboxItem } from '../features/bills/BillInbox';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/bills/new')({
  /*
   * `?inbox=<id>` keys an invoice from the bills inbox: its file sits beside the form instead of
   * the local-file picker, and saving writes the bill and files the invoice on it in one step.
   * Anything that is not a plausible id is the ordinary blank form.
   */
  validateSearch: (search: Record<string, unknown>): { inbox?: string } =>
    typeof search.inbox === 'string' && /^[0-9a-f-]{36}$/i.test(search.inbox)
      ? { inbox: search.inbox }
      : {},
  component: NewBillPage,
});

/**
 * KEYED BY THE INBOX ITEM, so "add bill and key the next one" starts the next invoice on a clean
 * form rather than carrying the last contractor, amount and description into it.
 */
function NewBillPage() {
  const { inbox } = Route.useSearch();
  return <BillForm key={inbox ?? 'new'} inboxItemId={inbox ?? null} />;
}

/**
 * Enter a supplier's invoice.
 *
 * THIS FORM POSTS NOTHING, and the panel on the right says so out loud. Keying an invoice is a
 * record of a piece of paper: the owners are not charged, the supplier is not paid, and nothing
 * appears on anybody's statement until somebody with a trust role approves it. Saying that on the
 * form is what stops a property manager either hesitating over it or believing they have just
 * spent an owner's money.
 *
 * A BILL IS ONE INVOICE, ONE SUPPLIER, ONE PROPERTY. The lawnmowing round billed monthly across
 * five houses is five bills, because owner attribution is the whole of how a bill is funded and
 * five houses can have five different owners. That is a sentence on the form rather than a
 * validation rule, because the rule is simply that the property is required.
 *
 * THE MAINTENANCE JOB IS OPTIONAL AND USUALLY BLANK. A rates notice, a lawnmowing round and the
 * annual gas certificate are all invoices nobody raised a job for; requiring one would make the
 * commonest case the awkward one. When there IS a job, linking it is what puts the invoice against
 * the figure somebody authorised — and the panel on the right says so, with the variance, before
 * anybody approves the bill.
 *
 * ARCHIVED SUPPLIERS ARE STILL SELECTABLE. An invoice arrives for work done before the agency
 * stopped using them, and a form that refused it would leave a debt the books did not record.
 * They are grouped separately so nobody picks one by accident.
 */
function BillForm({ inboxItemId }: { inboxItemId: string | null }) {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();
  const today = todayIso();

  const suppliers = useQuery(trpc.suppliers.list.queryOptions(undefined, { staleTime: 30_000 }));
  const properties = useQuery(trpc.property.list.queryOptions(undefined, { staleTime: 30_000 }));

  const [supplierId, setSupplierId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [dueOn, setDueOn] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [maintenanceJobId, setMaintenanceJobId] = useState('');

  /*
   * ONLY THIS PROPERTY'S JOBS, and only once a property is chosen. A bill charged to the owners of
   * one house while pointing at a job on another contradicts itself on the one link the job's
   * variance is computed from, and the server refuses it — so the picker never offers the mistake.
   */
  const jobs = useQuery(
    trpc.maintenance.list.queryOptions(
      { propertyId },
      { enabled: propertyId !== '', staleTime: 30_000 },
    ),
  );

  /*
   * THE INVOICE FILE, optional, attached the moment the bill exists. Two calls rather than one:
   * the bill and its paper are separate rows with separate refusals — an agency out of storage
   * must still be able to key the bill — so a refused file leaves a real bill and a notice, and
   * the bill page offers the upload again.
   */
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const attach = useAttachInvoice(null);

  const capture = useMutation(
    trpc.bills.capture.mutationOptions({
      meta: { errorTitle: 'Could not enter that invoice' },
      async onSuccess(created) {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.bills.list.queryKey() });
        if (invoiceFile !== null) await attach.choose(invoiceFile, created.id);
        // Straight to the record: the next thing anybody does with a keyed invoice is approve it,
        // and that is the page it is approved on.
        void navigate({ to: '/bills/$ref', params: { ref: created.ref } });
      },
    }),
  );

  /*
   * FROM THE INBOX: one call, not capture-then-attach — the bill, its invoice and the item's
   * outcome are written together (`enterBillFromInbox`), so a refused file cannot leave the item
   * waiting for a bill that already exists.
   */
  const inbox = useInboxItem(inboxItemId);
  const nextItem = inboxItemId === null ? null : nextWaitingItem(inbox.items, inboxItemId);
  const waitingAfterThis = inbox.items.filter(
    (item) => item.state === 'waiting' && item.id !== inboxItemId,
  ).length;
  const openNext = () => {
    if (nextItem) void navigate({ to: '/bills/new', search: { inbox: nextItem.id } });
    else void navigate({ to: '/bills', search: { tab: 'inbox' } });
  };

  const enter = useMutation(
    trpc.billInbox.enter.mutationOptions({
      meta: { errorTitle: 'Could not enter that invoice' },
      // Where to go next depends on which button saved it, so that is decided per call in `submit`.
      onSuccess() {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.bills.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.billInbox.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.billInbox.waitingCount.queryKey() });
      },
    }),
  );

  const allSuppliers = suppliers.data ?? [];
  const active = allSuppliers.filter((supplier) => supplier.status === 'active');
  const archived = allSuppliers.filter((supplier) => supplier.status !== 'active');
  const chosenSupplier = allSuppliers.find((supplier) => supplier.id === supplierId) ?? null;

  const propertyOptions = useMemo(
    () =>
      (properties.data ?? [])
        .map((property) => ({
          id: property.id,
          ref: property.ref,
          label: `${streetLine(property)}, ${property.city}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [properties.data],
  );

  /*
   * CANCELLED JOBS ARE LEFT OUT AND COMPLETED ONES ARE NOT, which looks inconsistent and is not:
   * the invoice for a finished job arrives days AFTER it was finished, so hiding completed jobs
   * would hide exactly the ones an operator is keying invoices against.
   */
  const openJobs = (jobs.data ?? []).filter((job) => job.state !== 'cancelled');
  const chosenJob = openJobs.find((job) => job.id === maintenanceJobId) ?? null;

  let amountCents: Money | null = null;
  try {
    amountCents = amount.trim() === '' ? null : parseAmount(amount);
  } catch {
    amountCents = null;
  }
  const amountIssue =
    amount.trim() === ''
      ? undefined
      : amountCents === null
        ? 'That is not an amount'
        : amountCents <= 0n
          ? 'A bill has to be above zero — a credit note reverses the bill it credits'
          : undefined;

  const dateIssue =
    dueOn !== '' && dueOn < invoiceDate
      ? 'The due date is before the invoice date — check which way round they go'
      : undefined;

  const ready =
    supplierId !== '' &&
    propertyId !== '' &&
    invoiceDate !== '' &&
    amountCents !== null &&
    amountIssue === undefined &&
    dateIssue === undefined &&
    description.trim() !== '';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || amountCents === null) return;

    const fields = {
      supplierId,
      propertyId,
      invoiceDate,
      amountCents: amountCents.toString(),
      description: description.trim(),
      ...(invoiceNumber.trim() === '' ? {} : { invoiceNumber: invoiceNumber.trim() }),
      ...(dueOn === '' ? {} : { dueOn }),
      ...(maintenanceJobId === '' ? {} : { maintenanceJobId }),
    };
    if (inboxItemId === null) {
      capture.mutate(fields);
      return;
    }
    // Which of the two submit buttons was pressed — the queue's "and key the next one" or not.
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const thenNext = submitter?.dataset.then === 'next';
    enter.mutate(
      { itemId: inboxItemId, ...fields },
      {
        onSuccess(created) {
          if (thenNext) openNext();
          else void navigate({ to: '/bills/$ref', params: { ref: created.ref } });
        },
      },
    );
  };

  const saving = capture.isPending || attach.working || enter.isPending;

  /*
   * AN ITEM ALREADY DEALT WITH — keyed from another screen, dismissed, or a stale link. Said as a
   * sentence with the way on, rather than a form that would be refused on save.
   */
  if (inboxItemId !== null && !inbox.list.isPending && inbox.item?.state !== 'waiting') {
    const gone = inbox.item;
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="New bill" subtitle="From the inbox" />
        <Panel className="mx-auto mt-4 flex max-w-lg flex-col items-start gap-3 p-6">
          <h2 className="text-[1.0625rem] font-bold">
            {gone === null
              ? 'That invoice is not in the inbox'
              : gone.state === 'entered'
                ? `That invoice has been keyed${gone.billRef ? ` as ${gone.billRef}` : ''}`
                : 'That invoice was dismissed'}
          </h2>
          <p className="text-[0.8125rem] text-ink-muted">
            {gone?.state === 'dismissed' && gone.dismissedReason
              ? `The reason given: “${gone.dismissedReason}”.`
              : 'Somebody may have dealt with it from another screen.'}
          </p>
          <div className="flex gap-2">
            {nextItem ? (
              <Button size="sm" onClick={openNext}>
                Key the next one
              </Button>
            ) : null}
            <Link to="/bills" search={{ tab: 'inbox' }}>
              <Button size="sm" variant="secondary">
                Back to the inbox
              </Button>
            </Link>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New bill"
        subtitle={
          inboxItemId === null
            ? 'One invoice, from one contractor, about one property'
            : waitingAfterThis > 0
              ? `From the inbox — ${String(waitingAfterThis)} more waiting after this one`
              : 'From the inbox — the last one waiting'
        }
        actions={
          inboxItemId === null ? (
            <Link to="/bills">
              <Button size="sm" variant="secondary">
                All bills
              </Button>
            </Link>
          ) : (
            <Link to="/bills" search={{ tab: 'inbox' }}>
              <Button size="sm" variant="secondary">
                Back to the inbox
              </Button>
            </Link>
          )
        }
      />

      <form onChange={unsaved.touch} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Panel className="p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Contractor" required hint="Who invoiced">
              {(props) => (
                <Select
                  {...props}
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                >
                  <option value="">Choose a contractor…</option>
                  {active.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name} — {SUPPLIER_TRADE_LABELS[supplier.trade]}
                    </option>
                  ))}
                  {archived.length > 0 ? (
                    <optgroup label="Archived">
                      {archived.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name} — {SUPPLIER_TRADE_LABELS[supplier.trade]}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                </Select>
              )}
            </Field>

            <Field label="Property" required hint="Whose money the work comes out of">
              {(props) => (
                <Select
                  {...props}
                  value={propertyId}
                  onChange={(e) => {
                    setPropertyId(e.target.value);
                    // The job belongs to the old property. Keeping it would build a bill the
                    // server refuses; better to clear it than to hand somebody a refusal.
                    setMaintenanceJobId('');
                  }}
                >
                  <option value="">Choose a property…</option>
                  {propertyOptions.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.label} · {property.ref}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          {openJobs.length > 0 ? (
            <div className="mt-4">
              <Field
                label="Maintenance job"
                hint="Optional — most invoices are not against a job. Linking one checks it against what was authorised"
              >
                {(props) => (
                  <Select
                    {...props}
                    value={maintenanceJobId}
                    onChange={(e) => setMaintenanceJobId(e.target.value)}
                  >
                    <option value="">Not against a job</option>
                    {openJobs.map((job) => (
                      <option key={job.id} value={job.id}>
                        {job.ref} · {job.summary}
                        {job.approvedLimitCents === null
                          ? ' (not approved)'
                          : ` (authorised $${formatAmount(BigInt(job.approvedLimitCents))})`}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          ) : null}
        </Panel>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            <Panel className="p-5">
              <PanelHeader title="The invoice" className="p-0 pb-3" />

              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Invoice number"
                  hint="Theirs, not ours. Leave blank if the docket has none"
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      className="tabular-nums"
                      placeholder="INV-1042"
                    />
                  )}
                </Field>

                <Field
                  label="Amount"
                  required
                  hint="Total on the invoice, GST included"
                  error={amountIssue}
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      inputMode="decimal"
                      className="tabular-nums"
                      placeholder="345.00"
                    />
                  )}
                </Field>

                <Field
                  label="Invoice date"
                  required
                  hint="The day it was issued — and the day whose owners are charged"
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Due" hint="When they expect to be paid" error={dateIssue}>
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      value={dueOn}
                      onChange={(e) => setDueOn(e.target.value)}
                    />
                  )}
                </Field>
              </div>

              <div className="mt-3">
                <Field
                  label="Work"
                  required
                  hint={`What was done, in the words the owner will read on their statement (${String(MAX_BILL_DESCRIPTION_LENGTH)} characters)`}
                >
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={2}
                      maxLength={MAX_BILL_DESCRIPTION_LENGTH}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Replace failed hot water cylinder element and thermostat"
                    />
                  )}
                </Field>
              </div>
            </Panel>
          </div>

          <div className="flex flex-col gap-4">
            {/*
             * FIRST IN THE SIDE COLUMN, and previewed from the file on this machine before
             * anything is uploaded: the figures on the left are typed FROM this paper, so it sits
             * beside them while they are.
             */}
            {inboxItemId === null ? (
              <ChosenInvoice
                file={invoiceFile}
                onChange={(chosen) => {
                  // A dropped file fires no change event for the form to hear.
                  unsaved.touch();
                  setInvoiceFile(chosen);
                }}
              />
            ) : inbox.item ? (
              <InboxInvoice
                item={inbox.item}
                onDismissed={() => {
                  unsaved.saved();
                  openNext();
                }}
              />
            ) : (
              <div className="h-[36rem] rounded-md bg-surface-sunk motion-safe:animate-pulse" />
            )}

            <Panel className="p-5">
              <PanelHeader title="Nothing is posted yet" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                Entering an invoice records it and nothing else. The owners are not charged, no
                money leaves the trust account, and nothing appears on a statement until somebody
                with a trust role approves it — which is a separate act, on the bill’s own page.
              </p>
              {amountCents !== null && amountIssue === undefined ? (
                <p className="mt-3 text-[0.8125rem] text-ink-muted">
                  When it is approved,{' '}
                  <Amount
                    value={amountCents}
                    currency
                    tone="neutral"
                    className="font-semibold text-ink"
                  />{' '}
                  will come off the owners of{' '}
                  {propertyOptions.find((property) => property.id === propertyId)?.label ??
                    'the property'}
                  , divided by whoever owned it on {invoiceDate || 'the invoice date'}.
                </p>
              ) : null}
            </Panel>

            {chosenJob ? (
              <Panel className="p-5">
                <PanelHeader title={`Against ${chosenJob.ref}`} className="p-0 pb-3" />
                {chosenJob.approvedLimitCents === null ? (
                  <p className="text-[0.8125rem] text-warn">
                    Nobody authorised a figure for this job. The invoice can still be entered and
                    approved — the work is done and the money is owed — but somebody should know
                    that it was never approved before the owners are charged.
                  </p>
                ) : (
                  <p className="text-[0.8125rem] text-ink-muted">
                    <Amount value={BigInt(chosenJob.approvedLimitCents)} currency tone="neutral" />{' '}
                    was authorised, and{' '}
                    <Amount value={BigInt(chosenJob.invoicedCents)} currency tone="neutral" /> has
                    been invoiced against it so far
                    {amountCents !== null && amountIssue === undefined ? (
                      <>
                        {' — '}
                        <Amount
                          value={BigInt(chosenJob.invoicedCents) + amountCents}
                          currency
                          tone="neutral"
                        />{' '}
                        with this one
                      </>
                    ) : null}
                    .
                  </p>
                )}
                <p className="mt-3 text-[0.75rem] text-ink-subtle">
                  Going over the authorised figure does not refuse anything. The work is done and
                  the money is owed — what the link buys is that somebody sees the difference before
                  the bill is approved.
                </p>
              </Panel>
            ) : null}

            <Panel className="p-5">
              <PanelHeader title="One invoice, one property" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                A round billed across several houses is entered once per house. Each property has
                its own owners, and it is their money the work comes out of — so an invoice covering
                five addresses is five expenses on five statements, not one.
              </p>
            </Panel>

            {chosenSupplier && !chosenSupplier.hasBankAccount ? (
              <Panel className="p-5">
                <PanelHeader title="Nowhere to pay them" className="p-0 pb-3" />
                <p className="text-[0.8125rem] text-ink-muted">
                  {chosenSupplier.name} has no bank account on file. The invoice can still be
                  entered and approved — a trust administrator records the account on their
                  contractor page before it can be paid, and a new one is held for 24 hours.
                </p>
              </Panel>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" disabled={!ready || saving}>
            {saving ? 'Saving' : 'Add bill'}
          </Button>
          {/*
           * THE QUEUE'S OWN BUTTON. Working a pile of invoices is key, save, next — and landing on
           * each new bill's page in between turns that into key, save, back, find the next one.
           */}
          {inboxItemId !== null && nextItem ? (
            <Button
              type="submit"
              size="lg"
              variant="secondary"
              disabled={!ready || saving}
              data-then="next"
            >
              Add bill and key the next one
            </Button>
          ) : null}
          <Link to="/bills" search={inboxItemId === null ? {} : { tab: 'inbox' }}>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
